/**
 * What the weekly checkup's last step — Program Update — tells the person, and whether
 * it asks them to accept a change.
 *
 * The possible answers:
 * - Undetermined: the progress check could not judge progress, so nothing is recommended.
 *   getUndeterminedReason says which line explains why (#6067).
 * - Adjust: the recommended daily target differs from the target in force by more than
 *   SMALL_CHANGE_FRACTION of that target; the person accepts or declines.
 * - AtMinimum: the change is that small, and it only exists because the solver's cut was raised
 *   back up to the minimum daily intake — the lowest target the solver sets for the person's
 *   bodyweight. The screen says the target cannot go lower (#5415).
 * - ChangeTooSmall: the change is that small for any other reason (#3528).
 * - OnTrack: nothing changed at all.
 * In the AtMinimum and ChangeTooSmall cases nothing is offered and the plan is not saved.
 *
 * Since #5456 a person without a coach may set their own target on that step. The page then
 * holds TWO outcomes: the one the app worked out (asked for without `userModifiedCalories`),
 * which the explanation text, the left button and the telemetry read, and the effective one
 * (asked for with it), which the Accept / Finish button reads. `checkupRecordValues` says what
 * the finished check-up records in each case.
 *
 * Kept free of Angular so a unit test can drive it (`weekly-checkup-outcome.test.ts`), the
 * same way `weekly-checkup-steps.ts` holds the step rules.
 */

import { readPositiveCalories } from '@cf-shared/data-model/log-weekly-checkup';
import { PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';

/** a change of this fraction of the target in force, or less, is too small to offer (1%) */
export const SMALL_CHANGE_FRACTION = 0.01;

export enum ProgramUpdateOutcome {
  Undetermined = 'Undetermined',
  Adjust = 'Adjust',
  AtMinimum = 'AtMinimum',
  ChangeTooSmall = 'ChangeTooSmall',
  OnTrack = 'OnTrack',
}

/** the parts of the progress check's result these rules read */
export interface ProgressCheck {
  needsAdjustment: boolean;
  actionDecided: boolean;
  message?: PlanUpdateUserMessage;
}

export interface ProgramUpdateOutcomeInputs {
  /** the progress check run when the checkup starts; undefined while it is still running */
  progressCheck: ProgressCheck | undefined;
  /** true when the solve at the calorie-baseline step lowered the target and the minimum daily intake raised it back */
  clampedToMinimumIntake: boolean;
  /** the daily target the solve at the calorie-baseline step produced */
  recommendedAvgCalories: number;
  /** the daily intake the person said they ate last week */
  calorieIntakeBaseline: number;
  /** the daily target in force before this checkup */
  prevWeekRecommendedCalories: number;
  /**
   * The daily target the person set for themselves with Modify (#5456), when they applied one.
   * Leave it out to get the outcome the APP worked out; pass it to get the outcome the Accept /
   * Finish button acts on.
   */
  userModifiedCalories?: number;
}

/**
 * True when the progress check could not judge progress: no change is needed and its message is
 * not AllOk. One exception (#6067): a deliberate "no change needed" labelled MeasureMoreOften is a
 * verdict, so it is judged like AllOk. An AllOk check that reached no verdict (a plan in its first
 * week, or one waiting to initialise its baseline) is deliberately NOT undetermined: the checkup
 * must still offer the calorie steps there, as it always has.
 */
export function isProgressCheckUndetermined(check: ProgressCheck): boolean {
  if (check.needsAdjustment || check.message === PlanUpdateUserMessage.AllOk) {
    return false;
  }
  // if the check decided "no change needed" and only asks for measurements more often
  if (check.actionDecided && check.message === PlanUpdateUserMessage.MeasureMoreOften) {
    return false;
  }
  return true;
}

export function getProgramUpdateOutcome(inputs: ProgramUpdateOutcomeInputs): ProgramUpdateOutcome {
  const check = inputs.progressCheck;
  // if the progress check could not judge progress
  if (check && isProgressCheckUndetermined(check)) {
    return ProgramUpdateOutcome.Undetermined;
  }
  // if the person applied their own target, there is a plan to save, however small the change:
  // without this a typed number within 1% of the target in force would be shown and then not saved
  if (readPositiveCalories(inputs.userModifiedCalories) !== undefined) {
    return ProgramUpdateOutcome.Adjust;
  }
  const recommended = Math.round(inputs.recommendedAvgCalories);
  const targetInForce = Math.round(inputs.prevWeekRecommendedCalories);
  const change = Math.abs(recommended - targetInForce);
  // if the target in force would move by more than the small-change limit (or there is no solve to compare yet), offer it
  if (!(change <= targetInForce * SMALL_CHANGE_FRACTION)) {
    return ProgramUpdateOutcome.Adjust;
  }
  // if the small change is only the minimum daily intake raising a cut back up
  if (inputs.clampedToMinimumIntake) {
    return ProgramUpdateOutcome.AtMinimum;
  }
  // if nothing moved and the intake eaten matched the target
  if (change === 0 && recommended === Math.round(inputs.calorieIntakeBaseline)) {
    return ProgramUpdateOutcome.OnTrack;
  }
  return ProgramUpdateOutcome.ChangeTooSmall;
}

/**
 * The number the app itself stands behind on the last step (#5456): the recommendation when the
 * app offers a change, and otherwise the target in force, because there the app keeps the target
 * and throws away the solver's few-kcal difference. Whole kcal.
 *
 * One definition, used for what the Modify fields open on, for the "The app recommended" line,
 * for the stored `appRecommendedAvgCalories`, and for deciding that a typed number is "the
 * app's number again".
 */
export function appRecommendedCalories(
  appOutcome: ProgramUpdateOutcome,
  recommendedAvgCalories: number,
  prevWeekRecommendedCalories: number,
): number {
  return Math.round(appOutcome === ProgramUpdateOutcome.Adjust ? recommendedAvgCalories : prevWeekRecommendedCalories);
}

/** what decides whether a person may set their own target on the check-up's last step (#5456) */
export interface OwnTargetConditions {
  /** the coaching dashboard switch, for the signed-in account */
  coachingDashboardOn: boolean;
  /** the calorie step ran in this check-up, so there is a solved plan to scale and a reported intake */
  hasSolvedPlan: boolean;
  /** the outcome the app worked out, before any modification */
  appOutcome: ProgramUpdateOutcome;
  /** the person has a coach: their coach decides, on the review screen */
  isCoached: boolean;
  /** a coach is filling in the wizard inside a client's account */
  isCoachManaging: boolean;
}

/**
 * Whether the last step offers Modify. Only with the switch on, only when the calorie step ran,
 * never when progress could not be judged, and never for a coached client or for a coach working
 * in a client's account: there the coach decides on the review screen, and a number set in the
 * wizard would be stored as the client's own.
 */
export function canModifyOwnTarget(conditions: OwnTargetConditions): boolean {
  return conditions.coachingDashboardOn
    && conditions.hasSolvedPlan
    && conditions.appOutcome !== ProgramUpdateOutcome.Undetermined
    && !conditions.isCoached
    && !conditions.isCoachManaging;
}

/** one choice listed on the check-up's last step while the coaching dashboard switch is on (#5456) */
export enum CheckupFinalChoice {
  /** take the change the app recommends; saves its plan */
  AcceptApp = 'accept-app',
  /** the app offers no change: keep its target; finishes the check-up */
  KeepAppTarget = 'keep-app-target',
  /** type a target of one's own; saves the solver's plan scaled to it */
  SetOwn = 'set-own',
  /** turn down the change the app recommends; stored as declined */
  KeepCurrent = 'keep-current',
}

/** what decides which choices the last step lists */
export interface FinalChoiceConditions {
  /** the coaching dashboard switch, for the signed-in account */
  coachingDashboardOn: boolean;
  /** a coached client sends the check-up to the coach instead of choosing */
  isCoachSubmission: boolean;
  /** the app offers a change (`ProgramUpdateOutcome.Adjust`) */
  isAdjustmentNeeded: boolean;
  /** the answer of `canModifyOwnTarget` */
  canModifyOwnTarget: boolean;
}

/**
 * The choices the last step lists, in order. Example: BORI (no coach) is told to go from
 * 2,450 to 2,300 kcal, so he gets Accept, Set my own and Keep my current. On track, he gets
 * Keep the app's target and Set my own. An empty list means there is nothing to choose and
 * the bar offers Finish: the switch is off, a coached client submits to the coach, or the
 * app offers no change and the person may not set their own target.
 */
export function finalCheckupChoices(conditions: FinalChoiceConditions): CheckupFinalChoice[] {
  if (!conditions.coachingDashboardOn || conditions.isCoachSubmission) return [];
  const ownTarget = conditions.canModifyOwnTarget ? [CheckupFinalChoice.SetOwn] : [];
  if (conditions.isAdjustmentNeeded) {
    return [CheckupFinalChoice.AcceptApp, ...ownTarget, CheckupFinalChoice.KeepCurrent];
  }
  return ownTarget.length > 0 ? [CheckupFinalChoice.KeepAppTarget, ...ownTarget] : [];
}

/** the per-meal calorie figures of one plan; which are set depends on calorie cycling */
export interface MealCalories {
  avg?: number;
  cut?: number;
  bulk?: number;
}

/** what the check-up page knows when it writes the finished check-up down */
export interface CheckupRecordInputs {
  /** `WeeklyCheckupCompletionAction.Accepted`, compared by the caller so this module stays free of the data model */
  isAccepted: boolean;
  /** the outcome the app worked out, before any modification */
  appOutcome: ProgramUpdateOutcome;
  /** the daily target the solve produced, as the page holds it */
  recommendedAvgCalories: number;
  /** the daily target in force before this check-up */
  prevWeekRecommendedCalories: number;
  usesCalorieCycling: boolean;
  /** the meal figures of the solver's plan */
  solverMeals: MealCalories;
  /** the meal figures of the plan in force */
  mealsInForce: MealCalories;
  /** the person's own target and the scaled plan's meal figures, when they applied one */
  modification?: { calories: number; meals: MealCalories };
}

/** the calorie fields of a finished check-up; a field left undefined is not written */
export interface CheckupRecordValues {
  recommendedAvgCalories: number;
  atMinimumIntake: boolean;
  changeTooSmall: boolean;
  avgMealCalories?: number;
  cutMealCalories?: number;
  bulkMealCalories?: number;
  userModifiedCalories?: number;
  appRecommendedAvgCalories?: number;
}

/** the meal fields a check-up stores for one plan: cut and bulk with calorie cycling, the average without */
function mealFields(meals: MealCalories, usesCalorieCycling: boolean): Pick<CheckupRecordValues, 'avgMealCalories' | 'cutMealCalories' | 'bulkMealCalories'> {
  return usesCalorieCycling
    ? { cutMealCalories: meals.cut, bulkMealCalories: meals.bulk }
    : { avgMealCalories: meals.avg };
}

/**
 * Which calorie figures a finished check-up records (#5456).
 *
 * - **The person accepted their own target**: the check-up records that target as the
 *   recommendation, the same number as `userModifiedCalories`, the app's own number as
 *   `appRecommendedAvgCalories`, and the scaled plan's meal figures. No "at minimum" or "change
 *   too small" mark, because the person did change the target.
 * - **Every other case** records exactly what it recorded before #5456, from the solver's own
 *   figures, whether or not a modification was applied. So a Decline after a Modify stores the
 *   same check-up a Decline stores without one. When the app kept the target in force (at minimum,
 *   or the change was too small), the target and meal figures in force are what gets recorded,
 *   never the few-kcal difference the solver handed back (#5415, #3528).
 *
 * Example: the app recommends 2,069 and BORI applies 2,120. Accept records 2,120, 2,120 and
 * 2,069. Decline records 2,069 and no trace of 2,120.
 */
export function checkupRecordValues(inputs: CheckupRecordInputs): CheckupRecordValues {
  if (inputs.modification && inputs.isAccepted) {
    return {
      recommendedAvgCalories: inputs.modification.calories,
      atMinimumIntake: false,
      changeTooSmall: false,
      ...mealFields(inputs.modification.meals, inputs.usesCalorieCycling),
      userModifiedCalories: inputs.modification.calories,
      appRecommendedAvgCalories: appRecommendedCalories(inputs.appOutcome, inputs.recommendedAvgCalories, inputs.prevWeekRecommendedCalories),
    };
  }
  const atMinimumIntake = inputs.appOutcome === ProgramUpdateOutcome.AtMinimum;
  const changeTooSmall = inputs.appOutcome === ProgramUpdateOutcome.ChangeTooSmall;
  const keepsTargetInForce = atMinimumIntake || changeTooSmall;
  return {
    recommendedAvgCalories: keepsTargetInForce ? inputs.prevWeekRecommendedCalories : inputs.recommendedAvgCalories,
    atMinimumIntake,
    changeTooSmall,
    ...mealFields(keepsTargetInForce ? inputs.mealsInForce : inputs.solverMeals, inputs.usesCalorieCycling),
  };
}

/** why the progress check could not judge progress; each reason has its own line on the screen (#6067) */
export enum UndeterminedReason {
  MeasureMoreOften = 'MeasureMoreOften',
  InsufficientFatnessConfidence = 'InsufficientFatnessConfidence',
  DataErratic = 'DataErratic',
  WeightTooFlat = 'WeightTooFlat',
  NotEnoughData = 'NotEnoughData',
}

/** maps the progress check's message to the line that explains an undetermined outcome (#6067) */
export function getUndeterminedReason(message: PlanUpdateUserMessage | undefined): UndeterminedReason {
  switch (message) {
    case PlanUpdateUserMessage.MeasureMoreOften:
      return UndeterminedReason.MeasureMoreOften;
    case PlanUpdateUserMessage.InsufficientFatnessConfidence:
      return UndeterminedReason.InsufficientFatnessConfidence;
    case PlanUpdateUserMessage.DataErratic:
      return UndeterminedReason.DataErratic;
    case PlanUpdateUserMessage.WeightTooFlat:
      return UndeterminedReason.WeightTooFlat;
    default:
      // NotEnoughData, LogWeighInNeeded, and an undecided check whose message stayed AllOk
      return UndeterminedReason.NotEnoughData;
  }
}
